// Phase 6 — Track C T-C2 + Track F T-F3. Providers — the top-level wrapper tree.
//
// Order matters:
//
//   ErrorBoundary           (T-F3: catches uncaught render errors at the root)
//     └── QueryClientProvider     (data fetching cache + global onError)
//           └── ThemeProvider     (sets data-theme on documentElement)
//                 └── BrowserRouter
//                       └── AuthProvider     (blocks initial render on /auth/me)
//                             └── NavigatorBridge   (wires queryClient → navigate + signOut)
//                                   └── Toaster
//                                   └── {children}  (the Routes element)
//
// The `<BrowserRouter>` is mounted by providers (rather than by router.tsx)
// so that AuthProvider + NavigatorBridge — both of which call hooks that
// depend on the router (useNavigate / location) — sit safely inside.
//
// `<NavigatorBridge/>` is a one-liner component that registers the
// queryClient's navigation + signOut callbacks the moment the auth
// context is available. It renders nothing.
//
// The ErrorBoundary is the outermost wrapper so it catches errors thrown
// by any provider (including the QueryClient, the router, the auth
// hydration). Per blueprint §20.3 the fallback never logs to a remote
// endpoint.

import * as React from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '../components/ui/Toaster.js';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.js';
import { ThemeProvider } from './theme.js';
import { ThemeSync } from '../components/ThemeSync.js';
import { AuthProvider, useAuth } from './AuthContext.js';
import { createQueryClient, registerAuthBridge } from './queryClient.js';

export interface ProvidersProps {
  readonly children: React.ReactNode;
  /**
   * Test hook — pass a pre-built QueryClient (e.g. one with retry: 0) so
   * tests don't need to reach for the fresh instance the prod path makes.
   */
  readonly queryClient?: QueryClient;
}

function NavigatorBridge(): null {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  React.useEffect(() => {
    registerAuthBridge({
      navigate: (to: string) => navigate(to),
      signOut,
    });
  }, [navigate, signOut]);
  return null;
}

/**
 * Phase 7 D3 — only run ThemeSync (which calls /api/users/me/preferences)
 * once the auth state resolves to authenticated. Keeps the network
 * surface minimal for unauthenticated visitors and avoids a 401 noise pulse
 * on /login.
 */
function ThemeSyncWhenAuthed(): JSX.Element | null {
  const { status } = useAuth();
  if (status !== 'authenticated') return null;
  return <ThemeSync />;
}

export function Providers({ children, queryClient }: ProvidersProps): JSX.Element {
  // useState lazily-initialises the QueryClient so it survives Strict Mode
  // double-mount in dev and the same instance is reused across remounts.
  const [client] = React.useState<QueryClient>(() => queryClient ?? createQueryClient());

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <NavigatorBridge />
              <ThemeSyncWhenAuthed />
              <Toaster />
              {children}
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
